import { Link, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X, ShoppingBag, User, Search, MessageCircle, Home as HomeIcon, LayoutGrid } from "lucide-react";
import { useTheme } from "@/context/ThemeContext";
import { useAuth, isStaff } from "@/context/AuthContext";
import api from "@/lib/api";
import Brand from "@/components/public/Brand";
import { Button } from "@/components/ui/button";

const NAV = [
  { label: "Home", to: "/" },
  { label: "Products", to: "/products" },
  { label: "Categories", to: "/categories" },
  { label: "Deals", to: "/deals" },
  { label: "How It Works", to: "/page/how" },
  { label: "About", to: "/page/about" },
  { label: "Contact", to: "/contact" },
];

export function ThemeToggle() {
  return null;
}

function Header() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const { user } = useAuth();
  const navigate = useNavigate();

  const submit = (e) => {
    e.preventDefault();
    navigate(`/products?search=${encodeURIComponent(q)}`);
  };

  const portalLink = isStaff(user) ? "/admin" : "/portal";

  return (
    <header className="sticky top-0 z-50 border-b border-border bg-background/80 glass">
      <div className="mx-auto flex max-w-7xl items-center gap-4 px-4 py-3 sm:px-6">
        <Link to="/" data-testid="brand-logo" className="flex items-center gap-2">
          <Brand height={38} />
        </Link>

        <nav className="ml-4 hidden items-center gap-1 lg:flex">
          {NAV.map((n) => (
            <Link key={n.to} to={n.to} data-testid={`nav-${n.label.toLowerCase().replace(/ /g, "-")}`}
              className="rounded-md px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground">
              {n.label}
            </Link>
          ))}
        </nav>

        <form onSubmit={submit} className="ml-auto hidden items-center md:flex">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="header-search"
              placeholder="Search products..."
              className="h-9 w-44 rounded-full border border-border bg-secondary pl-9 pr-3 text-sm outline-none focus:w-56 focus:border-primary transition-[width,border-color] lg:w-52" />
          </div>
        </form>

        <div className="ml-auto flex items-center gap-2 md:ml-3">
          {user ? (
            <Button asChild size="sm" data-testid="header-portal-btn">
              <Link to={portalLink}><User className="mr-1.5 h-4 w-4" />{isStaff(user) ? "Dashboard" : "My Portal"}</Link>
            </Button>
          ) : (
            <Button asChild size="sm" data-testid="header-login-btn">
              <Link to="/login"><User className="mr-1.5 h-4 w-4" />Login</Link>
            </Button>
          )}
          <button className="grid h-9 w-9 place-items-center rounded-full border border-border lg:hidden" onClick={() => setOpen(!open)} data-testid="mobile-menu-btn">
            {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
        </div>
      </div>
      {open && (
        <div className="border-t border-border bg-background lg:hidden">
          <div className="grid gap-1 p-4">
            <form onSubmit={(e) => { submit(e); setOpen(false); }} className="mb-2">
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="mobile-header-search" placeholder="Search products..." className="h-11 w-full rounded-full border border-border bg-secondary pl-9 pr-3 text-sm outline-none focus:border-primary" />
              </div>
            </form>
            {NAV.map((n) => (
              <Link key={n.to} to={n.to} onClick={() => setOpen(false)}
                className="rounded-md px-3 py-2.5 text-sm font-medium hover:bg-secondary">{n.label}</Link>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}

function Footer() {
  const [settings, setSettings] = useState(null);
  useEffect(() => { api.get("/public/settings").then((r) => setSettings(r.data)).catch(() => {}); }, []);
  const s = settings || {};
  return (
    <footer className="mt-24 border-t border-border bg-secondary/40">
      <div className="mx-auto grid max-w-7xl gap-10 px-6 py-14 md:grid-cols-4">
        <div>
          <div className="flex items-center gap-2">
            <Brand height={40} textClass="font-display text-lg font-extrabold" />
          </div>
          <p className="mt-4 text-sm text-muted-foreground">{s.tagline || "Your Dreams, Our Deals"}. Pakistan's trusted installment marketplace.</p>
          <p className="mt-3 text-sm text-muted-foreground">{s.address}</p>
        </div>
        <div>
          <h4 className="overline text-muted-foreground">Company</h4>
          <div className="mt-4 grid gap-2 text-sm">
            <Link to="/page/about" className="text-muted-foreground hover:text-primary">About Us</Link>
            <Link to="/page/careers" className="text-muted-foreground hover:text-primary">Careers</Link>
            <Link to="/page/how" className="text-muted-foreground hover:text-primary">How It Works</Link>
            <Link to="/contact" className="text-muted-foreground hover:text-primary">Contact Us</Link>
          </div>
        </div>
        <div>
          <h4 className="overline text-muted-foreground">Support</h4>
          <div className="mt-4 grid gap-2 text-sm">
            <Link to="/page/customer_care" className="text-muted-foreground hover:text-primary">Customer Care</Link>
            <Link to="/complaints" className="text-muted-foreground hover:text-primary">Launch Complaint</Link>
            <Link to="/page/privacy" className="text-muted-foreground hover:text-primary">Privacy Policy</Link>
            <Link to="/page/terms" className="text-muted-foreground hover:text-primary">Terms & Conditions</Link>
          </div>
        </div>
        <div>
          <h4 className="overline text-muted-foreground">Contact</h4>
          <div className="mt-4 grid gap-2 text-sm text-muted-foreground">
            <span>UAN: {s.uan}</span>
            <span>Phone: {s.phone}</span>
            <span>Email: {s.email}</span>
            <span>{s.business_hours}</span>
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            {(s.payment_methods || []).filter((p) => p.enabled).map((p) => (
              p.logo
                ? <img key={p.key} src={p.logo} alt={p.name} title={p.name} className="h-7 w-auto rounded bg-white object-contain px-1.5 py-0.5" />
                : <span key={p.key} className="rounded-md border border-border bg-card px-2 py-1 text-xs font-medium">{p.name}</span>
            ))}
          </div>
        </div>
      </div>
      {s.footer_logos && (s.footer_logos.fbr || s.footer_logos.secp || s.footer_logos.jamapunji) && (
        <div className="border-t border-border py-6">
          <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-center gap-8 px-6">
            {["fbr", "secp", "jamapunji"].map((k) => (s.footer_logos[k] ? <img key={k} src={s.footer_logos[k]} alt={k.toUpperCase()} className="h-12 w-auto object-contain sm:h-14" data-testid={`footer-logo-${k}`} /> : null))}
          </div>
        </div>
      )}
      <div className="border-t border-border py-5 text-center text-xs text-muted-foreground">
        © {new Date().getFullYear()} MANHA DIGITAL. All rights reserved.
      </div>
    </footer>
  );
}

function MobileBottomNav() {
  const { pathname } = useLocation();
  const { user } = useAuth();
  const account = user ? (isStaff(user) ? "/admin" : "/portal") : "/login";
  const items = [
    { to: "/", label: "Home", icon: HomeIcon },
    { to: "/products", label: "Shop", icon: ShoppingBag },
    { to: "/categories", label: "Categories", icon: LayoutGrid },
    { to: account, label: user ? "Account" : "Login", icon: User },
  ];
  const active = (to) => (to === "/" ? pathname === "/" : pathname.startsWith(to));
  return (
    <nav className="fixed bottom-0 left-0 right-0 z-50 grid grid-cols-4 border-t border-border bg-background/95 backdrop-blur lg:hidden" data-testid="mobile-bottom-nav">
      {items.map((it) => {
        const I = it.icon;
        return (
          <Link key={it.label} to={it.to} data-testid={`bottomnav-${it.label.toLowerCase()}`}
            className={`flex flex-col items-center gap-0.5 py-2.5 text-[11px] font-medium transition-colors ${active(it.to) ? "text-primary" : "text-muted-foreground"}`}>
            <I className="h-5 w-5" />{it.label}
          </Link>
        );
      })}
    </nav>
  );
}

export default function PublicLayout({ children }) {
  const [s, setS] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  useEffect(() => {
    api.get("/public/settings").then((r) => {
      setS(r.data);
      if (r.data?.popup_enabled && r.data?.popup_image && !sessionStorage.getItem("md_popup_seen")) setShowPopup(true);
    }).catch(() => {});
  }, []);
  const wa = (s?.social?.whatsapp || "").replace(/[^0-9]/g, "");
  const closePopup = () => { sessionStorage.setItem("md_popup_seen", "1"); setShowPopup(false); };
  return (
    <div className="min-h-screen bg-background pb-16 lg:pb-0">
      {s?.headline && s?.headline_enabled !== false && (
        <div className="bg-primary px-4 py-2 text-center text-xs font-semibold text-primary-foreground sm:text-sm" data-testid="announcement-bar">{s.headline}</div>
      )}
      <Header />
      <main>{children}</main>
      <Footer />
      <MobileBottomNav />
      {wa && (
        <a href={`https://wa.me/${wa}`} target="_blank" rel="noreferrer" data-testid="whatsapp-btn" aria-label="Chat on WhatsApp"
          className="fixed bottom-20 right-4 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-[#25D366] text-white shadow-lg transition-transform hover:scale-110 lg:bottom-5 lg:right-5">
          <MessageCircle className="h-7 w-7" fill="currentColor" />
        </a>
      )}
      {showPopup && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-4" data-testid="popup-modal" onClick={closePopup}>
          <div className="relative max-h-[85vh] max-w-lg overflow-hidden rounded-2xl bg-card" onClick={(e) => e.stopPropagation()}>
            <button onClick={closePopup} data-testid="popup-close" className="absolute right-3 top-3 grid h-8 w-8 place-items-center rounded-full bg-black/50 text-white hover:bg-black/70"><X className="h-4 w-4" /></button>
            {s.popup_link
              ? <a href={s.popup_link} target="_blank" rel="noreferrer"><img src={s.popup_image} alt="Announcement" className="max-h-[85vh] w-full object-contain" /></a>
              : <img src={s.popup_image} alt="Announcement" className="max-h-[85vh] w-full object-contain" />}
          </div>
        </div>
      )}
    </div>
  );
}
